<template>
  <div ref="root" class="sasel" :class="rootClass">
    <!-- trigger (div, not button, so the clear button can nest validly) -->
    <div
      ref="trigger"
      class="sasel__field"
      :class="{ 'sasel__field--ph': isEmpty, 'sasel__field--open': open }"
      role="button"
      tabindex="0"
      :aria-label="ariaLabel || placeholder"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown="onTriggerKeydown"
      >
      <svg v-if="prependIcon" class="sasel__picon" viewBox="0 0 24 24" aria-hidden="true"><path :d="prependIcon" /></svg>
      <span class="sasel__label">{{ label }}</span>
      <button v-if="clearable && !isEmpty" type="button" class="sasel__clear" aria-label="Limpiar" @click.stop="clear">
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
      <svg class="sasel__caret" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
    </div>

    <Teleport to="body">
      <div v-if="open" ref="pop" class="sasel__pop sa-scope" :style="[popStyle, positioned ? null : { visibility: 'hidden' }]" role="listbox">
        <div v-if="searchable" class="sasel__searchwrap">
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            class="sasel__search"
            :placeholder="searchPlaceholder || 'Buscar…'"
            @keydown="onSearchKeydown"
            >
        </div>
        <ul ref="list" class="sasel__list">
          <li v-if="!filtered.length" class="sasel__empty">Sin resultados</li>
          <li
            v-for="(it, i) in filtered"
            :key="it.value"
            class="sasel__opt"
            :class="{ 'sasel__opt--active': i === activeIndex, 'sasel__opt--on': isSelected(it.value) }"
            role="option"
            :aria-selected="isSelected(it.value)"
            @click="choose(it)"
            @mousemove="activeIndex = i"
            >
            <span class="sasel__optlabel">{{ it.title }}</span>
            <svg v-if="multiple && isSelected(it.value)" class="sasel__check" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
          </li>
        </ul>
        <div v-if="truncated" class="sasel__hint">Seguí escribiendo para ver más…</div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
// Lightweight headless select / autocomplete — replaces v-select & v-autocomplete
// so Vuetify's VMenu/VList/VOverlay/virtual-scroll machinery can leave the bundle.
// Popup is teleported to <body> to avoid clipping by scroll/overflow containers.
import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number, Array, null], default: null },
  items: { type: Array, default: () => [] }, // [{ title, value }]
  placeholder: { type: String, default: 'Seleccionar' },
  searchPlaceholder: { type: String, default: '' },
  multiple: { type: Boolean, default: false },
  searchable: { type: Boolean, default: false },
  maxVisible: { type: Number, default: 8 }, // cap rendered options in searchable mode so the popup stays compact
  clearable: { type: Boolean, default: false },
  prependIcon: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  variant: { type: String, default: 'outlined' }, // 'outlined' | 'plain'
  dense: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

const root = ref(null);
const trigger = ref(null);
const pop = ref(null);
const list = ref(null);
const searchInput = ref(null);
const open = ref(false);
const query = ref('');
const activeIndex = ref(-1);
const popStyle = ref({});
const positioned = ref(false); // gate the popup's first paint until coords are computed

const rootClass = computed(() => [`sasel--${props.variant}`, { 'sasel--dense': props.dense, 'sasel--open': open.value }]);

const selectedArray = computed(() => (props.multiple && Array.isArray(props.modelValue) ? props.modelValue : []));
const isEmpty = computed(() =>
  props.multiple ? selectedArray.value.length === 0 : props.modelValue == null || props.modelValue === ''
);

const label = computed(() => {
  if (props.multiple) {
    const arr = selectedArray.value;
    if (!arr.length) return props.placeholder;
    const first = props.items.find((it) => it.value === arr[0]);
    const firstLabel = first ? first.title : arr[0];
    return arr.length > 1 ? `${firstLabel} +${arr.length - 1}` : firstLabel;
  }
  if (isEmpty.value) return props.placeholder;
  const sel = props.items.find((it) => it.value === props.modelValue);
  return sel ? sel.title : String(props.modelValue);
});

const matched = computed(() => {
  if (!props.searchable || !query.value.trim()) return props.items;
  const q = query.value.trim().toLowerCase();
  return props.items.filter((it) => (it.title || '').toLowerCase().includes(q));
});
// only the searchable combobox caps its list — plain selects render every option
const filtered = computed(() => (props.searchable ? matched.value.slice(0, props.maxVisible) : matched.value));
const truncated = computed(() => props.searchable && matched.value.length > filtered.value.length);

const isSelected = (v) => (props.multiple ? selectedArray.value.includes(v) : props.modelValue === v);

watch(query, () => { activeIndex.value = filtered.value.length ? 0 : -1; });

const MAX_POP_H = 300; // hard cap so a long list scrolls instead of spanning the screen
const GAP = 6;

function position() {
  const el = trigger.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight;
  // use the real rendered height once the popup exists, capped; otherwise assume the cap
  const h = Math.min(MAX_POP_H, pop.value ? pop.value.scrollHeight : MAX_POP_H);
  const roomBelow = vh - r.bottom - GAP;
  // drop down when there's room (or more room below than above); else flip up,
  // anchoring the popup's BOTTOM edge just above the trigger so there's never a gap
  const dropDown = roomBelow >= h || roomBelow >= r.top - GAP;
  let top = dropDown ? r.bottom + GAP : r.top - GAP - h;
  top = Math.max(8, Math.min(top, vh - h - 8)); // keep within the viewport
  popStyle.value = {
    position: 'fixed',
    left: `${Math.round(r.left)}px`,
    width: `${Math.round(r.width)}px`,
    top: `${Math.round(top)}px`,
    maxHeight: `${MAX_POP_H}px`,
  };
  positioned.value = true;
}

function toggle() { if (open.value) close(); else openMenu(); }

async function openMenu() {
  // the trigger is already mounted, so compute coords BEFORE the popup paints —
  // this prevents the one-frame flash where an unpositioned popup lands mid-screen
  positioned.value = false;
  position();
  open.value = true;
  query.value = '';
  activeIndex.value = props.multiple ? -1 : filtered.value.findIndex((it) => it.value === props.modelValue);
  await nextTick();
  position();
  window.addEventListener('scroll', position, true);
  window.addEventListener('resize', position);
  document.addEventListener('mousedown', onDocDown, true);
  // preventScroll so focusing the search field never yanks the page
  if (props.searchable && searchInput.value) searchInput.value.focus({ preventScroll: true });
  else scrollActiveIntoView();
}

function close() {
  if (!open.value) return;
  open.value = false;
  window.removeEventListener('scroll', position, true);
  window.removeEventListener('resize', position);
  document.removeEventListener('mousedown', onDocDown, true);
}

function onDocDown(e) {
  if (root.value?.contains(e.target) || pop.value?.contains(e.target)) return;
  close();
}

function choose(it) {
  if (props.multiple) {
    const arr = selectedArray.value.slice();
    const i = arr.indexOf(it.value);
    if (i >= 0) arr.splice(i, 1); else arr.push(it.value);
    emit('update:modelValue', arr);
  } else {
    emit('update:modelValue', it.value);
    close();
    trigger.value?.focus();
  }
}

function clear() { emit('update:modelValue', props.multiple ? [] : null); }

function move(d) {
  const n = filtered.value.length;
  if (!n) return;
  activeIndex.value = (activeIndex.value + d + n) % n;
  scrollActiveIntoView();
}

async function scrollActiveIntoView() {
  await nextTick();
  list.value?.children?.[activeIndex.value]?.scrollIntoView({ block: 'nearest' });
}

function onTriggerKeydown(e) {
  if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
    e.preventDefault();
    if (open.value) { if (e.key === 'ArrowDown') move(1); }
    else openMenu();
  } else if (e.key === 'Escape') {
    close();
  }
}

function onSearchKeydown(e) {
  if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
  else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
  else if (e.key === 'Enter') { e.preventDefault(); const it = filtered.value[activeIndex.value]; if (it) choose(it); }
  else if (e.key === 'Escape') { e.preventDefault(); close(); trigger.value?.focus(); }
}

onBeforeUnmount(close);
</script>

<style scoped>
.sasel { position: relative; }

.sasel__field {
  display: flex; align-items: center; gap: 8px; width: 100%;
  cursor: pointer; user-select: none; color: var(--sa-ink);
  font-size: 15px; font-weight: 500; line-height: 1.2;
}
.sasel__field--ph { color: var(--sa-ink-soft); font-weight: 500; } /* AA contrast on white */

/* outlined — mirrors Vuetify variant="outlined" rounded="lg" */
.sasel--outlined .sasel__field {
  border: 1px solid var(--sa-line); border-radius: 8px;
  background: var(--sa-surface); padding: 0 12px; min-height: 48px;
  transition: border-color .15s ease;
}
.sasel--outlined .sasel__field:hover { border-color: #cfc7b8; }
.sasel--outlined .sasel__field--open,
.sasel--outlined .sasel__field:focus-visible { border-color: var(--sa-ink); outline: none; }
.sasel--outlined.sasel--dense .sasel__field { min-height: 40px; }

/* plain — inline, borderless (used inside the search card) */
.sasel--plain .sasel__field { padding: 4px 0; min-height: 32px; outline: none; }

.sasel__label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left; }
.sasel__picon { width: 18px; height: 18px; flex: none; color: var(--sa-ink-faint); fill: currentColor; }
.sasel__caret { width: 18px; height: 18px; flex: none; color: var(--sa-ink-faint); transition: transform .2s ease; }
.sasel--open .sasel__caret { transform: rotate(180deg); }
.sasel__clear {
  flex: none; width: 20px; height: 20px; border: none; background: transparent; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: var(--sa-ink-faint); padding: 0;
}
.sasel__clear:hover { color: var(--sa-ink); }
.sasel__clear svg { width: 14px; height: 14px; }

/* popup (teleported, but keeps the scoped data attr so these styles apply) */
.sasel__pop {
  position: fixed; top: 0; left: 0; /* coords come from inline style; this guards against any unpositioned paint */
  z-index: 10010; /* above SaDialog (10000) so a select inside a dialog isn't hidden */
  background: #fff; border: 1px solid var(--sa-line); border-radius: 10px;
  box-shadow: 0 18px 40px -16px rgba(40, 33, 20, .34); overflow: hidden;
  display: flex; flex-direction: column;
}
.sasel__searchwrap { padding: 8px; border-bottom: 1px solid #f0ebe2; flex: none; }
.sasel__search {
  width: 100%; border: 1px solid var(--sa-line); border-radius: 7px; padding: 9px 11px;
  font-size: 14px; font-family: inherit; color: var(--sa-ink); background: #fbf9f5; outline: none;
}
.sasel__search:focus { border-color: var(--sa-ink); }

.sasel__list { list-style: none; margin: 0; padding: 5px; overflow-y: auto; flex: 1; }
.sasel__empty { padding: 14px 12px; color: var(--sa-ink-faint); font-size: 13.5px; text-align: center; }
.sasel__hint { flex: none; padding: 9px 12px; border-top: 1px solid #f0ebe2; color: var(--sa-ink-faint); font-size: 12px; text-align: center; }
.sasel__opt {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 10px 11px; border-radius: 7px; cursor: pointer; font-size: 14px; color: var(--sa-ink);
}
.sasel__opt--active { background: #f4f0e8; }
.sasel__opt--on { font-weight: 600; }
.sasel__optlabel { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sasel__check { width: 17px; height: 17px; flex: none; color: var(--sa-brand); }
</style>
